import { createBrowserRouter, Navigate } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import { NotFound } from './pages/NotFound';
import { Unauthorized } from './pages/Unauthorized';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Core pages
import Profile from './pages/core/Profile';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminLogs from './pages/admin/Logs';
import AdminDebug from './pages/admin/Debug';

// Agent pages
import AgentDashboard from './pages/agent/Dashboard';
import AgentClients from './pages/agent/Clients';
import AgentTransactions from './pages/agent/Transactions';
import AgentStats from './pages/agent/Stats';

// Clicker pages
import ClickerDashboard from './pages/clicker/Dashboard';
import ClickerTasks from './pages/clicker/Tasks';
import ClickerHistory from './pages/clicker/History';
import ClickerStats from './pages/clicker/Stats';

// Trading pages
import TradingDashboard from './pages/trading/Dashboard';

// Monitoring pages
import MonitoringDashboard from './pages/monitoring/Dashboard';

// Get the current subdomain
const getSubdomain = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'main';
  const parts = hostname.split('.');
  return parts.length > 2 ? parts[0] : 'main';
};

const createProtectedRoutes = (children: React.ReactNode, role?: string) => ({
  element: (
    <ProtectedRoute requiredRole={role}>
      <MainLayout>{children}</MainLayout>
    </ProtectedRoute>
  ),
});

const routes = {
  main: [
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/',
      ...createProtectedRoutes(
        <>
          <Navigate to="/dashboard" />
          <Profile />
          <NotFound />
        </>
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '/profile', element: <Profile /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  admin: [
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/',
      ...createProtectedRoutes(
        <>
          <AdminDashboard />
          <AdminUsers />
          <AdminLogs />
          <AdminDebug />
          <NotFound />
        </>,
        'admin'
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '/dashboard', element: <AdminDashboard /> },
        { path: '/users', element: <AdminUsers /> },
        { path: '/logs', element: <AdminLogs /> },
        { path: '/debug', element: <AdminDebug /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  agent: [
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/',
      ...createProtectedRoutes(
        <>
          <AgentDashboard />
          <AgentClients />
          <AgentTransactions />
          <AgentStats />
          <NotFound />
        </>,
        'agent'
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '/dashboard', element: <AgentDashboard /> },
        { path: '/clients', element: <AgentClients /> },
        { path: '/transactions', element: <AgentTransactions /> },
        { path: '/stats', element: <AgentStats /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  trading: [
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/',
      ...createProtectedRoutes(
        <>
          <TradingDashboard />
          <NotFound />
        </>,
        'trader'
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '/dashboard', element: <TradingDashboard /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  monitor: [
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/',
      ...createProtectedRoutes(
        <>
          <MonitoringDashboard />
          <NotFound />
        </>,
        'monitor'
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard" /> },
        { path: '/dashboard', element: <MonitoringDashboard /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ],
};

const subdomain = getSubdomain();
const router = createBrowserRouter(routes[subdomain] || routes.main);

export default router;
